/**
 * AgentModelPicker — dropdown that lists the models an agent CLI can target.
 *
 * Backed by `useAgentModelsStore` (24h cache, refresh button). Used in both
 * project-scope and app-scope contexts:
 *
 *  - Project agent options (ProjectWizard / Workspace edit panel / Settings →
 *    Edit Project) pass the per-project `preferredModel`; the dropdown adds a
 *    "Use global default" entry that resolves to `defaultAgentModel[agentType]`.
 *
 *  - App Settings → default-agent panel passes the global default value
 *    directly with no fallback option visible.
 *
 * Renders nothing when the agent's `modelFlag` is undefined or when the
 * model list comes back empty (e.g. agent CLI not installed). The caller
 * always knows the agentType, so we can early-return on `modelFlag`.
 */
import { useEffect, useMemo } from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';
import { AGENT_CLI_CONFIG } from '@/lib/agentCliConfig';
import { useAgentModelsStore, type AgentModel } from '@/stores/agentModelsStore';
import { useSettingsStore } from '@/stores/settingsStore';

interface AgentModelPickerProps {
  agentType: string;
  /** Current value (project-scope: `preferredModel`; app-scope: the default itself). */
  value: string | undefined;
  /** Update handler. Pass empty string to clear / fall back. */
  onChange: (value: string) => void;
  /** Whether to show "Use global default (xyz)" as an option. Project-scope only. */
  showFallback?: boolean;
  /** Disable while the parent's save flow is in flight. */
  disabled?: boolean;
}

export default function AgentModelPicker({
  agentType,
  value,
  onChange,
  showFallback = false,
  disabled = false,
}: AgentModelPickerProps) {
  const config = AGENT_CLI_CONFIG[agentType];
  const cache = useAgentModelsStore((s) => s.cache[agentType]);
  const inFlight = useAgentModelsStore((s) => s.inFlight[agentType]);
  const list = useAgentModelsStore((s) => s.list);
  const globalDefault = useSettingsStore((s) => s.agent.defaultModel[agentType]);

  // Trigger a fetch on mount + whenever the agent type changes. The store
  // self-caches for 24h so this is cheap on repeat mounts.
  useEffect(() => {
    if (!config?.modelFlag) return;
    void list(agentType);
  }, [agentType, config?.modelFlag, list]);

  const grouped = useMemo(() => {
    if (!cache?.models) return [] as { provider: string; models: AgentModel[] }[];
    const map = new Map<string, AgentModel[]>();
    for (const m of cache.models) {
      const arr = map.get(m.provider) ?? [];
      arr.push(m);
      map.set(m.provider, arr);
    }
    return Array.from(map.entries())
      .map(([provider, models]) => ({ provider, models: models.sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => a.provider.localeCompare(b.provider));
  }, [cache]);

  // The agent doesn't take a model flag at all — render nothing.
  if (!config?.modelFlag) return null;

  // No models available (and no error) — most likely the agent CLI isn't
  // installed yet, or it's an agent we don't have a list command for. Show
  // a static hint so users know they can still type a model via customFlags.
  const noModels = cache && cache.models.length === 0 && !cache.error;

  return (
    <div className="form-control">
      <div className="flex items-center justify-between mb-1">
        <label className="label py-0">
          <span className="label-text text-sm">Model</span>
        </label>
        <button
          type="button"
          className="btn btn-ghost btn-xs gap-1"
          onClick={() => void list(agentType, true)}
          disabled={disabled || !!inFlight}
          title="Refresh model list"
        >
          {inFlight ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>
      <select
        className="select select-bordered select-sm w-full"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || !cache}
      >
        {showFallback && (
          <option value="">
            Use global default{globalDefault ? ` (${globalDefault})` : ''}
          </option>
        )}
        {!showFallback && <option value="">Agent's own default</option>}
        {grouped.map((group) => (
          <optgroup key={group.provider} label={group.provider}>
            {group.models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <label className="label py-0">
        <span className="label-text-alt text-base-content/60 text-xs">
          {!cache && 'Loading model list…'}
          {cache?.error && (
            <span className="text-warning">
              Couldn't fetch models — {cache.error}
            </span>
          )}
          {noModels && !cache.error && (
            <>
              No models reported. Install / authenticate the agent CLI, or type a
              <code className="mx-1">{config.modelFlag} &lt;id&gt;</code>
              in the custom flags box.
            </>
          )}
          {cache && cache.models.length > 0 && (
            <>
              {cache.models.length} model{cache.models.length === 1 ? '' : 's'} cached. Auto-refreshes every 24h.
            </>
          )}
        </span>
      </label>
    </div>
  );
}
